import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import posthog from 'posthog-js';
import { useUserTier, UserTier } from '../hooks/useUserTier';

const ANONYMOUS_CREDITS = 3;
const FREE_TIER_CREDITS = 10;
const RESET_HOURS = 24;
const STORAGE_KEY_ANONYMOUS = 'credits_anonymous';
const STORAGE_KEY_FREE = 'credits_free';

type CreditData = {
  credits: number;
  lastReset: number;
};

function getTimeUntilReset(lastReset: number) {
  const now = Date.now();
  const resetTime = lastReset + RESET_HOURS * 60 * 60 * 1000;
  const diff = resetTime - now;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { hours, minutes };
}

interface CreditsContextValue {
  credits: number;
  consumeCredit: () => boolean;
  resetCredits: (time?: number) => void;
  timeUntilReset: { hours: number; minutes: number } | null;
  userTier: UserTier;
  hasUnlimitedCredits: boolean;
}

const CreditsContext = createContext<CreditsContextValue | undefined>(
  undefined,
);

export const CreditsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading } = useAuth0();
  const userTier = useUserTier();
  
  const [credits, setCredits] = useState<number>(ANONYMOUS_CREDITS);
  const [lastReset, setLastReset] = useState<number>(Date.now());
  const [timeUntilReset, setTimeUntilReset] = useState<{ hours: number; minutes: number } | null>(null);

  const hasUnlimitedCredits = userTier === 'pro';
  const storageKey = userTier === 'anonymous' ? STORAGE_KEY_ANONYMOUS : STORAGE_KEY_FREE;
  const defaultCredits = userTier === 'anonymous' ? ANONYMOUS_CREDITS : FREE_TIER_CREDITS;

  const resetCredits = useCallback((time: number = Date.now()) => {
    if (hasUnlimitedCredits) return;

    setCredits(defaultCredits);
    setLastReset(time);

    if (userTier === 'free') {
      setTimeUntilReset(getTimeUntilReset(time));
    } else {
      setTimeUntilReset(null);
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({ credits: defaultCredits, lastReset: time }),
    );
  }, [hasUnlimitedCredits, defaultCredits, userTier, storageKey]);

  // Initialize from localStorage based on user tier
  useEffect(() => {
    if (isLoading) return;

    const now = Date.now();

    // Pro users have unlimited credits
    if (hasUnlimitedCredits) {
      setCredits(999); // Display value for pro users
      setTimeUntilReset(null);
      return;
    }

    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const parsed: CreditData = JSON.parse(stored);
        const hoursPassed = (now - parsed.lastReset) / (1000 * 60 * 60);

        // Free tier: reset after 24 hours
        // Anonymous: no reset
        if (userTier === 'free' && hoursPassed >= RESET_HOURS) {
          resetCredits(now);
        } else {
          setCredits(parsed.credits);
          setLastReset(parsed.lastReset);
          if (userTier === 'free') {
            setTimeUntilReset(getTimeUntilReset(parsed.lastReset));
          }
        }
      } catch (err) {
        resetCredits(now);
      }
    } else {
      resetCredits(now);
    }
  }, [isLoading, userTier, hasUnlimitedCredits, storageKey, resetCredits]);

  // Update timer every minute for free tier
  useEffect(() => {
    if (userTier !== 'free') return;

    const interval = setInterval(() => {
      setTimeUntilReset(getTimeUntilReset(lastReset));
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [lastReset, userTier]);

  const consumeCredit = useCallback(() => {
    // Pro users never consume credits
    if (hasUnlimitedCredits) {
      return true;
    }

    if (credits > 0) {
      const updated = credits - 1;
      setCredits(updated);
      localStorage.setItem(
        storageKey,
        JSON.stringify({ credits: updated, lastReset }),
      );

      // Track when credits are exhausted
      if (updated === 0) {
        const eventData = {
          credits: 0,
          userTier,
          lastReset: new Date(lastReset).toISOString(),
          timeUntilReset: timeUntilReset ? `${timeUntilReset.hours}h ${timeUntilReset.minutes}m` : 'N/A',
        };
        posthog.capture('credits_exhausted', eventData);
      }

      return true;
    }

    // User tried to consume credit but already at 0
    posthog.capture('credit_limit_reached', { userTier });
    return false;
  }, [hasUnlimitedCredits, credits, storageKey, lastReset, timeUntilReset, userTier]);

  return (
    <CreditsContext.Provider
      value={{ 
        credits, 
        consumeCredit, 
        resetCredits, 
        timeUntilReset,
        userTier,
        hasUnlimitedCredits
      }}
    >
      {children}
    </CreditsContext.Provider>
  );
};

export const useCreditsContext = () => {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCreditsContext must be used within a CreditsProvider');
  }
  return context;
};
